// Finds the right picture for every card, token, the mat and the card back.
// If an image file is missing (or not set yet), a placeholder is drawn instead,
// so the game always works while you're still making art.
import { drawSymbol } from './tokenGlyphs.ts';
import { cardFaceSrc, hasFace, lighterOf, pictureNames } from './cardFace.ts';
import { CONFIG } from '../config.ts';
import { type CardDef, type StatIcon, cardStats, costLabel, titleCase, cardNo } from '../shared/cards.ts';
import { abilityTerms, statusPieces } from './cardText.ts';
import type { TokenDef } from '../shared/tokens.ts';
import { CARD_DB, TOKEN_DB } from './data.ts';
import { TOKEN_RECIPES } from './table/tokenRecipes.ts';

const TOKEN_COLORS: Record<string, string> = {
  damage: '#c2483c',
  resource: '#d6a443',
  status: '#3f8db0',
  misc: '#7f8791',
  tier: '#c9952e',
  affiliation: '#5f503c',
  'special-hit': '#c2483c',
  ...Object.fromEntries(Object.entries(CONFIG.placeholderCards.affiliation).map(([a, c]) => [`aff-${a}`, c])),
  gerzite: CONFIG.colors.gerziteBar,
  'dark-gerzite': CONFIG.colors.darkGerziteBar,
};

/** The color a token is known by: its placeholder chip, and its tag in a card's close-up. */
export function tokenColor(def: { id: string; effect: string; category: string }): string {
  return TOKEN_COLORS[def.id] ?? TOKEN_RECIPES[def.effect]?.color ?? TOKEN_COLORS[def.category] ?? TOKEN_COLORS.misc;
}

/** '#4a98cc' -> '74,152,204' for use in rgba(...) */
function hexToRgb(hex: string) {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? [...h].map((c) => c + c).join('') : h;
  const n = Number.parseInt(full, 16);
  return `${(n >> 16) & 255},${(n >> 8) & 255},${n & 255}`;
}

const DISPLAY_FONT = '"Marcellus SC", Georgia, serif';
const BODY_FONT = '"Alegreya Sans", "Trebuchet MS", sans-serif';

export const CARD_PX = { w: 500, h: 700 };

function makeCanvas(w: number, h: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  return [c, c.getContext('2d')!];
}

function toBlobUrl(c: HTMLCanvasElement): Promise<string> {
  return new Promise((resolve) => {
    c.toBlob((blob) => resolve(blob ? URL.createObjectURL(blob) : c.toDataURL()), 'image/png');
  });
}

function box(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, r);
}

// ---------------- placeholder drawings ----------------

/** How light a color is, 0 (black) to 1 (white): decides whether text on it is light or dark. */
function lightness(hex: string) {
  const [r, g, b] = hexToRgb(hex).split(',').map((n) => {
    const v = Number(n) / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * The colors of a placeholder card (CONFIG.placeholderCards): the card is its rarity's color (Emperors have
 * their own), with that rarity's `accent` if it has one. `affiliation` colors its type bar and text box: one
 * color for each affiliation the card has, or the plain "no affiliation" color.
 */
export function placeholderColors(card: CardDef): { main: string; accent: string | null; affiliation: string[] } {
  const look = CONFIG.placeholderCards;
  const emperor = card.types.includes('emperor');
  const affiliation = card.affiliations.map((a) => look.affiliation[a]).filter((color): color is string => !!color);
  return {
    main: emperor ? look.emperor.main : (look.rarity[card.rarity] ?? look.noRarity),
    accent: emperor ? look.emperor.accent : (look.accent[card.rarity] ?? null),
    affiliation: affiliation.length ? affiliation : [look.noAffiliation],
  };
}

/** Light or dark lettering, whichever reads on these colors. */
function inkOn(colors: string[]) {
  const l = colors.reduce((sum, color) => sum + lightness(color), 0) / colors.length;
  // the contrast each ink would have on that color: the stronger one wins
  const light = (lightness('#f4efe4') + 0.05) / (l + 0.05);
  const dark = (l + 0.05) / (lightness('#1f1e1b') + 0.05);
  return light >= dark ? '#f4efe4' : '#1f1e1b';
}

// ---- the stats printed on a placeholder card: a small picture and a number for each ----
/** The color each stat's little picture is drawn in. Counters use their token's own color. */
export function statColor(icon: StatIcon): string {
  const token = (id: string) => {
    const def = TOKEN_DB.byId.get(id);
    return def ? tokenColor(def) : null;
  };
  switch (icon) {
    case 'health':
    case 'sp':
      return '#f05b55';
    case 'heals':
      return '#5fd08a';
    case 'shield':
      return token('shield') ?? '#7fb6e6';
    case 'buffer':
      return token('buffer') ?? '#ff8f7a';
    case 'count':
      return token('count') ?? '#5a9cf0';
    case 'damage':
      return '#f1ece1';
    case 'thorns':
      return token('thorns') ?? '#9ad06a';
    case 'range':
      return token('range') ?? '#e9c46a';
    case 'turns':
      return '#bfe9ff';
    case 'removal':
      return '#f05b55';
    default:
      return '#e6b54f';
  }
}
/** Draws one stat's picture, centred on (cx, cy), about `s` pixels across. */
function drawStatIcon(ctx: CanvasRenderingContext2D, icon: StatIcon, cx: number, cy: number, s: number, color: string) {
  const r = s / 2;
  ctx.save();
  ctx.translate(cx, cy);
  ctx.fillStyle = color;
  ctx.strokeStyle = color;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.lineWidth = Math.max(2, s * 0.12);
  const heart = () => {
    ctx.beginPath();
    ctx.moveTo(0, r * 0.9);
    ctx.bezierCurveTo(-r * 1.5, -r * 0.1, -r * 0.75, -r * 1.15, 0, -r * 0.4);
    ctx.bezierCurveTo(r * 0.75, -r * 1.15, r * 1.5, -r * 0.1, 0, r * 0.9);
    ctx.fill();
  };
  const word = (text: string, size: number, ink: string) => {
    ctx.fillStyle = ink;
    ctx.font = `800 ${size}px ${BODY_FONT}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, 0, size * 0.06);
  };
  switch (icon) {
    case 'health':
      heart();
      break;
    case 'heals':
      heart();
      ctx.strokeStyle = '#10301d';
      ctx.lineWidth = s * 0.13;
      ctx.beginPath();
      ctx.moveTo(-r * 0.36, -r * 0.08);
      ctx.lineTo(r * 0.36, -r * 0.08);
      ctx.moveTo(0, -r * 0.44);
      ctx.lineTo(0, r * 0.28);
      ctx.stroke();
      break;
    case 'sp':
      ctx.beginPath();
      ctx.arc(0, 0, r, 0, Math.PI * 2);
      ctx.fill();
      word('SP', s * 0.5, '#2a0d0c');
      break;
    case 'shield':
      ctx.beginPath();
      ctx.moveTo(-r * 0.82, -r * 0.82);
      ctx.quadraticCurveTo(0, -r * 1.12, r * 0.82, -r * 0.82);
      ctx.lineTo(r * 0.82, r * 0.05);
      ctx.quadraticCurveTo(r * 0.7, r * 0.72, 0, r);
      ctx.quadraticCurveTo(-r * 0.7, r * 0.72, -r * 0.82, r * 0.05);
      ctx.closePath();
      ctx.fill();
      break;
    case 'buffer':
      ctx.beginPath();
      ctx.moveTo(-r * 0.5, -r * 0.85);
      ctx.lineTo(r * 0.95, -r * 0.55);
      ctx.lineTo(r * 0.5, r * 0.85);
      ctx.lineTo(-r * 0.95, r * 0.55);
      ctx.closePath();
      ctx.fill();
      break;
    case 'count':
      ctx.beginPath();
      ctx.roundRect(-r * 0.9, -r * 0.9, r * 1.8, r * 1.8, r * 0.3);
      ctx.fill();
      word('×', s * 0.8, '#ffffff');
      break;
    case 'damage':
      ctx.rotate(-Math.PI / 4);
      ctx.beginPath(); // blade, guard, grip
      ctx.moveTo(-r * 0.12, -r * 1.05);
      ctx.lineTo(r * 0.12, -r * 1.05);
      ctx.lineTo(r * 0.16, r * 0.3);
      ctx.lineTo(-r * 0.16, r * 0.3);
      ctx.closePath();
      ctx.fill();
      ctx.fillRect(-r * 0.5, r * 0.3, r, r * 0.2);
      ctx.fillRect(-r * 0.11, r * 0.5, r * 0.22, r * 0.55);
      break;
    case 'thorns':
      ctx.beginPath();
      for (let i = 0; i <= 6; i++) {
        const x = -r + (i / 6) * 2 * r;
        const y = i % 2 ? -r * 0.3 : r * 0.3;
        if (i) ctx.lineTo(x, y);
        else ctx.moveTo(x, y);
      }
      ctx.stroke();
      for (const x of [-r * 0.66, 0, r * 0.66]) {
        ctx.beginPath();
        ctx.moveTo(x, -r * 0.3);
        ctx.lineTo(x, -r * 0.95);
        ctx.stroke();
      }
      break;
    case 'range':
      ctx.beginPath(); // the bow, its string, and an arrow laid across it
      ctx.arc(-r * 0.25, 0, r * 0.95, -Math.PI * 0.42, Math.PI * 0.42);
      ctx.stroke();
      ctx.lineWidth = Math.max(1.5, s * 0.07);
      ctx.beginPath();
      ctx.moveTo(-r * 0.25 + Math.cos(-Math.PI * 0.42) * r * 0.95, Math.sin(-Math.PI * 0.42) * r * 0.95);
      ctx.lineTo(-r * 0.25 + Math.cos(Math.PI * 0.42) * r * 0.95, Math.sin(Math.PI * 0.42) * r * 0.95);
      ctx.stroke();
      ctx.lineWidth = Math.max(2, s * 0.1);
      ctx.beginPath();
      ctx.moveTo(-r * 0.95, 0);
      ctx.lineTo(r, 0);
      ctx.moveTo(r * 0.55, -r * 0.32);
      ctx.lineTo(r, 0);
      ctx.lineTo(r * 0.55, r * 0.32);
      ctx.stroke();
      break;
    case 'turns':
      ctx.beginPath();
      ctx.arc(0, r * 0.08, r * 0.82, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, -r * 0.4);
      ctx.lineTo(0, r * 0.08);
      ctx.lineTo(r * 0.38, r * 0.08);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(-r * 0.62, -r * 0.82, r * 0.2, 0, Math.PI * 2);
      ctx.arc(r * 0.62, -r * 0.82, r * 0.2, 0, Math.PI * 2);
      ctx.fill();
      break;
    case 'removal':
      ctx.beginPath();
      ctx.arc(0, 0, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = s * 0.14;
      ctx.beginPath();
      ctx.moveTo(-r * 0.4, -r * 0.4);
      ctx.lineTo(r * 0.4, r * 0.4);
      ctx.moveTo(r * 0.4, -r * 0.4);
      ctx.lineTo(-r * 0.4, r * 0.4);
      ctx.stroke();
      break;
    default: // replay: a ring with an arrowhead
      ctx.lineWidth = s * 0.2;
      ctx.beginPath();
      ctx.arc(0, 0, r * 0.72, -Math.PI * 0.35, Math.PI * 1.35);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(r * 0.05, -r * 1.05);
      ctx.lineTo(r * 0.7, -r * 0.62);
      ctx.lineTo(r * 0.02, -r * 0.2);
      ctx.closePath();
      ctx.fill();
  }
  ctx.restore();
}

// ---- a description laid out in a box: wrapped, as large as fits, status effects in colored boxes ----
interface TextAtom {
  text: string;
  token?: TokenDef;
  bold?: boolean;
  space: boolean; // a space comes before it (atoms without one stay glued to the atom before: "Stun," "Shield.")
}
/** One paragraph as atoms: words, and status-effect names kept whole. A leading "Entrance:" is bold. */
function atomsOf(paragraph: string, allBold = false): TextAtom[] {
  const out: TextAtom[] = [];
  let space = false;
  // a heading (an Emperor ability's name) is all bold and is never read for status effects: "Shield Bearer" is a name
  for (const piece of allBold ? [{ text: paragraph } as { text: string; token?: TokenDef }] : statusPieces(paragraph)) {
    if (piece.token) {
      out.push({ text: piece.text, token: piece.token, bold: allBold, space });
      space = false;
      continue;
    }
    for (const part of piece.text.split(/(\s+)/)) {
      if (!part) continue;
      if (/^\s+$/.test(part)) {
        space = true;
        continue;
      }
      out.push({ text: part, bold: allBold, space });
      space = false;
    }
  }
  const label = out[0]?.text.match(/^([A-Za-z]+):$/);
  if (label && CONFIG.cardText.labels.includes(label[1])) out[0].bold = true;
  if (out[0]) out[0].space = false;
  return out;
}
const PILL_PAD = 0.22; // side padding of a status box, in font sizes
function atomWidth(ctx: CanvasRenderingContext2D, a: TextAtom, size: number) {
  ctx.font = `${a.bold || a.token ? 700 : 400} ${size}px ${BODY_FONT}`;
  return ctx.measureText(a.text).width + (a.token ? size * PILL_PAD * 2 : 0);
}
/** Breaks paragraphs into lines no wider than `maxWidth` at this font size. A blank entry is a paragraph gap. */
function wrapAtoms(ctx: CanvasRenderingContext2D, paragraphs: TextAtom[][], size: number, maxWidth: number): (TextAtom[] | null)[] {
  ctx.font = `400 ${size}px ${BODY_FONT}`;
  const spaceW = ctx.measureText(' ').width;
  const lines: (TextAtom[] | null)[] = [];
  paragraphs.forEach((atoms, pi) => {
    if (pi) lines.push(null);
    let line: TextAtom[] = [];
    let width = 0;
    for (let i = 0; i < atoms.length; ) {
      // a unit: this atom and every atom glued to it
      let j = i + 1;
      while (j < atoms.length && !atoms[j].space) j++;
      const unit = atoms.slice(i, j);
      const unitW = unit.reduce((sum, a) => sum + atomWidth(ctx, a, size), 0);
      if (line.length && width + spaceW + unitW > maxWidth) {
        lines.push(line);
        line = [];
        width = 0;
      }
      width += (line.length ? spaceW : 0) + unitW;
      line.push(...unit);
      i = j;
    }
    if (line.length) lines.push(line);
  });
  return lines;
}
/**
 * Prints paragraphs inside a box, as large as fits (cardText.maxFont down to minFont), left-aligned and
 * centred top to bottom. Status effects are drawn in a box of their token's color.
 */
function drawRichText(ctx: CanvasRenderingContext2D, paragraphs: TextAtom[][], x: number, y: number, w: number, h: number, ink: string) {
  if (!paragraphs.length) return;
  const { maxFont, minFont } = CONFIG.cardText;
  let size = maxFont;
  let lines = wrapAtoms(ctx, paragraphs, size, w);
  const heightOf = (ls: (TextAtom[] | null)[], sz: number) => ls.reduce((sum, l) => sum + (l ? sz * 1.24 : sz * 0.42), 0);
  while (size > minFont && heightOf(lines, size) > h) {
    size -= 1;
    lines = wrapAtoms(ctx, paragraphs, size, w);
  }
  const lineH = size * 1.24;
  ctx.font = `400 ${size}px ${BODY_FONT}`;
  const spaceW = ctx.measureText(' ').width;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  let cy = y + Math.max(0, (h - heightOf(lines, size)) / 2) + lineH / 2;
  for (const line of lines) {
    if (!line) {
      cy += size * 0.42;
      continue;
    }
    if (cy - lineH / 2 > y + h + 2) break; // more than even the smallest print can hold: the close-up has it all
    let cx = x;
    line.forEach((a, i) => {
      if (i && a.space) cx += spaceW;
      const aw = atomWidth(ctx, a, size);
      if (a.token) {
        const color = tokenColor(a.token);
        ctx.fillStyle = color;
        box(ctx, cx, cy - size * 0.56, aw, size * 1.12, size * 0.26);
        ctx.fill();
        ctx.strokeStyle = 'rgba(255,255,255,0.6)'; // a thin edge, so a box still shows on a text box of its own color
        ctx.lineWidth = Math.max(1, size * 0.06);
        ctx.stroke();
        ctx.fillStyle = lightness(color) < 0.3 ? '#ffffff' : '#16171a';
        ctx.font = `700 ${size}px ${BODY_FONT}`;
        ctx.fillText(a.text, cx + size * PILL_PAD, cy + size * 0.05);
      } else {
        ctx.fillStyle = ink;
        ctx.font = `${a.bold ? 700 : 400} ${size}px ${BODY_FONT}`;
        ctx.fillText(a.text, cx, cy + size * 0.05);
      }
      cx += aw;
    });
    cy += lineH;
  }
}

function drawCard(card: CardDef): HTMLCanvasElement {
  const { w, h } = CARD_PX;
  const [c, ctx] = makeCanvas(w, h);
  const { main, accent, affiliation } = placeholderColors(card);
  // the type bar and the text box share the affiliation's color: two affiliations get half each
  const affiliationPanel = (x: number, y: number, pw: number, ph: number) => {
    ctx.save();
    box(ctx, x, y, pw, ph, 10);
    ctx.clip();
    affiliation.forEach((color, i) => {
      ctx.fillStyle = color;
      ctx.fillRect(x + (pw / affiliation.length) * i, y, pw / affiliation.length + 1, ph);
    });
    ctx.restore();
    ctx.strokeStyle = 'rgba(255,255,255,0.3)';
    ctx.lineWidth = 2;
    box(ctx, x, y, pw, ph, 10);
    ctx.stroke();
  };
  // a very dark card (Emperors, Ancient) gets pale panels, so the name and type bars still stand out
  const veryDark = lightness(main) < 0.035;
  const bar = accent ?? (veryDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.45)');
  ctx.fillStyle = '#131417';
  ctx.fillRect(0, 0, w, h);
  // the card: its rarity's color, a little deeper toward the bottom
  box(ctx, 14, 14, w - 28, h - 28, 20);
  ctx.fillStyle = main;
  ctx.fill();
  const shade = ctx.createLinearGradient(0, 0, 0, h);
  shade.addColorStop(0, 'rgba(255,255,255,0.06)');
  shade.addColorStop(0.45, 'rgba(0,0,0,0)');
  shade.addColorStop(1, 'rgba(0,0,0,0.28)');
  ctx.fillStyle = shade;
  ctx.fill();
  if (accent) {
    // a rarity with a second color: a band of it round the card, and a wedge in each corner
    ctx.save();
    box(ctx, 14, 14, w - 28, h - 28, 20);
    ctx.clip();
    ctx.fillStyle = accent;
    for (const [x, y, sx, sy] of [[14, 14, 1, 1], [w - 14, 14, -1, 1], [14, h - 14, 1, -1], [w - 14, h - 14, -1, -1]] as const) {
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + sx * 74, y);
      ctx.lineTo(x, y + sy * 74);
      ctx.closePath();
      ctx.fill();
    }
    ctx.restore();
    ctx.strokeStyle = accent;
    ctx.lineWidth = 12;
    box(ctx, 26, 26, w - 52, h - 52, 14);
    ctx.stroke();
  }
  ctx.strokeStyle = 'rgba(255,255,255,0.35)';
  ctx.lineWidth = 3;
  box(ctx, 30, 30, w - 60, h - 60, 12);
  ctx.stroke();

  // name bar + cost
  ctx.fillStyle = bar;
  box(ctx, 44, 44, w - 88, 64, 10);
  ctx.fill();
  ctx.fillStyle = accent ? inkOn([accent]) : '#f4efe4';
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';
  ctx.font = `34px ${DISPLAY_FONT}`;
  ctx.fillText(card.name, 62, 78, 300);
  ctx.beginPath();
  ctx.arc(w - 82, 76, 26, 0, Math.PI * 2);
  ctx.fillStyle = card.costDark ? '#9a78d4' : '#e6b54f'; // gold = Gerzite, violet = a cost paid in Dark Gerzite
  ctx.fill();
  ctx.fillStyle = card.costDark ? '#1c1030' : '#2a1e08';
  ctx.textAlign = 'center';
  ctx.font = `700 ${costLabel(card).length > 2 ? 20 : 28}px ${BODY_FONT}`;
  ctx.fillText(costLabel(card), w - 82, 78); // a number, "3/7" for a card with two costs, "X" for one worked out as it is played

  // The art window: a card with no picture needs little room for one, so it is kept short and the room
  // goes to the description. The card's stats sit in a row along its bottom edge.
  const stats = cardStats(card);
  const artTop = 124;
  const artBottom = 318;
  ctx.fillStyle = veryDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.3)';
  box(ctx, 44, artTop, w - 88, artBottom - artTop, 10);
  ctx.fill();
  const numberMid = stats.length ? artTop + (artBottom - artTop - 58) / 2 : (artTop + artBottom) / 2;
  ctx.fillStyle = 'rgba(255,255,255,0.88)';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  // a spawn-only card has no set number: it shows what it is instead
  if (card.spawnOnly) {
    ctx.font = `54px ${DISPLAY_FONT}`;
    ctx.fillText('Summoned', w / 2, numberMid + 4);
  } else {
    ctx.font = `92px ${DISPLAY_FONT}`;
    ctx.fillText(cardNo(card), w / 2, numberMid + 6);
  }
  if (stats.length) {
    // one chip per stat: its picture, then its number. The row shrinks to fit when a card has many.
    const rowY = artBottom - 34;
    const measure = (size: number) => {
      ctx.font = `700 ${size}px ${BODY_FONT}`;
      return stats.map((st) => size * 1.25 + 8 + ctx.measureText(st.value).width + 20);
    };
    let size = 27;
    let widths = measure(size);
    const gap = 8;
    while (size > 15 && widths.reduce((a2, b2) => a2 + b2, 0) + gap * (stats.length - 1) > w - 104) {
      size -= 1;
      widths = measure(size);
    }
    let x = w / 2 - (widths.reduce((a2, b2) => a2 + b2, 0) + gap * (stats.length - 1)) / 2;
    stats.forEach((st, i) => {
      const cw = widths[i];
      ctx.fillStyle = 'rgba(0,0,0,0.55)';
      box(ctx, x, rowY - size * 0.82, cw, size * 1.64, size * 0.5);
      ctx.fill();
      drawStatIcon(ctx, st.icon, x + 10 + size * 0.6, rowY, size * 1.12, statColor(st.icon));
      ctx.fillStyle = st.dark ? '#c9a7ff' : '#ffffff'; // a cost paid in Dark Gerzite is violet
      ctx.font = `700 ${size}px ${BODY_FONT}`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText(st.value, x + 10 + size * 1.25 + 6, rowY + size * 0.06);
      x += cw + gap;
    });
  }

  // The bar above the description, in the affiliation's color: the card's type, then its descriptor lines
  // (Troop · Single Damage · Target · Ground). Rarity and affiliation aren't written out: the color of the
  // card says the one, the color of this bar and the text box the other.
  const barTop = 328;
  const barH = 46;
  affiliationPanel(44, barTop, w - 88, barH);
  const ink = inkOn(affiliation);
  ctx.fillStyle = ink;
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';
  const typeText = titleCase(card.types.join(' / '));
  const traitText = card.traits.length ? ` · ${card.traits.join(' · ')}` : '';
  // both parts on one line; squeezed a little, evenly, if a card has a lot to say
  ctx.font = `700 23px ${BODY_FONT}`;
  const typeW = ctx.measureText(typeText).width;
  ctx.font = `400 21px ${BODY_FONT}`;
  const traitW = ctx.measureText(traitText).width;
  const squeeze = Math.min(1, (w - 120) / (typeW + traitW));
  ctx.font = `700 23px ${BODY_FONT}`;
  ctx.fillText(typeText, 60, barTop + barH / 2 + 1, typeW * squeeze);
  ctx.font = `400 21px ${BODY_FONT}`;
  if (traitText) ctx.fillText(traitText, 60 + typeW * squeeze, barTop + barH / 2 + 1, traitW * squeeze);

  // The text box, in the affiliation's color: the description, or an Emperor's two abilities.
  const boxTop = barTop + barH + 10;
  const boxH = h - 44 - boxTop;
  affiliationPanel(44, boxTop, w - 88, boxH);
  const paragraphs: TextAtom[][] = [];
  if (card.emperorAbilities.length) {
    for (const ab of card.emperorAbilities) {
      paragraphs.push(atomsOf(`${ab.name} (${abilityTerms(ab)})`, true));
      if (ab.text) paragraphs.push(atomsOf(ab.text));
    }
  } else {
    for (const part of card.text.split('\n')) if (part.trim()) paragraphs.push(atomsOf(part.trim()));
  }
  drawRichText(ctx, paragraphs, 58, boxTop + 10, w - 116, boxH - 20, ink);
  return c;
}

function drawCardBack(): HTMLCanvasElement {
  const { w, h } = CARD_PX;
  const [c, ctx] = makeCanvas(w, h);
  ctx.fillStyle = '#131417';
  ctx.fillRect(0, 0, w, h);
  const g = ctx.createRadialGradient(w / 2, h / 2, 40, w / 2, h / 2, h * 0.6);
  g.addColorStop(0, '#3a3550');
  g.addColorStop(1, '#16141f');
  box(ctx, 14, 14, w - 28, h - 28, 20);
  ctx.fillStyle = g;
  ctx.fill();
  ctx.strokeStyle = '#c9a24a';
  ctx.lineWidth = 6;
  box(ctx, 34, 34, w - 68, h - 68, 14);
  ctx.stroke();
  ctx.lineWidth = 2;
  box(ctx, 48, 48, w - 96, h - 96, 10);
  ctx.stroke();
  // crest
  ctx.save();
  ctx.translate(w / 2, h / 2);
  for (const [size, alpha] of [[150, 0.25], [110, 0.5], [70, 0.9]] as const) {
    ctx.beginPath();
    ctx.moveTo(0, -size);
    ctx.lineTo(size * 0.7, 0);
    ctx.lineTo(0, size);
    ctx.lineTo(-size * 0.7, 0);
    ctx.closePath();
    ctx.strokeStyle = `rgba(201,162,74,${alpha})`;
    ctx.lineWidth = 4;
    ctx.stroke();
  }
  ctx.restore();
  ctx.fillStyle = 'rgba(201,162,74,0.8)';
  ctx.textAlign = 'center';
  ctx.font = `26px ${DISPLAY_FONT}`;
  ctx.fillText('Placeholder back', w / 2, h - 90);
  return c;
}

/** Where the pips sit on each face of a die, on a 3 x 3 grid (0 = left/top, 1 = middle, 2 = right/bottom). */
const PIPS: Record<number, [number, number][]> = {
  1: [[1, 1]],
  2: [[0, 0], [2, 2]],
  3: [[0, 0], [1, 1], [2, 2]],
  4: [[0, 0], [2, 0], [0, 2], [2, 2]],
  5: [[0, 0], [2, 0], [1, 1], [0, 2], [2, 2]],
  6: [[0, 0], [2, 0], [0, 1], [2, 1], [0, 2], [2, 2]],
};

/** One face of a six-sided die: a colored square with rounded shading and white pips. */
export function drawDieFace(color: string, value: number, px = 128, corner = 0): HTMLCanvasElement {
  const [c, ctx] = makeCanvas(px, px);
  ctx.fillStyle = color;
  box(ctx, 0, 0, px, px, corner * px);
  ctx.fill();
  // lighter in the middle, darker toward the edges, so the cube reads as rounded
  const g = ctx.createRadialGradient(px * 0.42, px * 0.38, px * 0.1, px / 2, px / 2, px * 0.78);
  g.addColorStop(0, 'rgba(255,255,255,0.22)');
  g.addColorStop(0.55, 'rgba(255,255,255,0)');
  g.addColorStop(1, 'rgba(0,0,0,0.42)');
  ctx.fillStyle = g;
  ctx.fill();
  const r = px * 0.095;
  for (const [gx, gy] of PIPS[value] ?? []) {
    const x = px * (0.25 + gx * 0.25);
    const y = px * (0.25 + gy * 0.25);
    ctx.beginPath();
    ctx.arc(x, y + px * 0.012, r, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(0,0,0,0.35)';
    ctx.fill();
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fillStyle = CONFIG.dice.pips;
    ctx.fill();
  }
  return c;
}

/** The color of a die, from the dice settings in config.ts. */
export function dieColor(tokenId: string) {
  return CONFIG.dice.colors[tokenId] ?? '#8a8f99';
}

export function drawToken(def: TokenDef): HTMLCanvasElement {
  const [c, ctx] = makeCanvas(256, 256);
  if (def.category === 'dice') {
    // the spawn menu shows a die as its "5" face
    ctx.drawImage(drawDieFace(dieColor(def.id), 5, 208, 0.16), 24, 24);
    return c;
  }
  const color = tokenColor(def);
  if (def.shape === 'cutout') {
    // placeholder for shaped tokens: a crystal on a transparent background
    const pts: [number, number][] = [[128, 10], [212, 74], [198, 188], [128, 246], [58, 188], [44, 74]];
    ctx.beginPath();
    pts.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
    ctx.closePath();
    const gem = ctx.createLinearGradient(44, 10, 212, 246);
    gem.addColorStop(0, '#ffffffcc');
    gem.addColorStop(0.25, color);
    gem.addColorStop(1, '#00000099');
    ctx.fillStyle = color;
    ctx.fill();
    ctx.fillStyle = gem;
    ctx.fill();
    ctx.strokeStyle = 'rgba(255,255,255,0.45)';
    ctx.lineWidth = 4;
    ctx.beginPath();
    for (const [x, y] of pts) {
      ctx.moveTo(128, 128);
      ctx.lineTo(x, y);
    }
    ctx.stroke();
    ctx.lineWidth = 6;
    ctx.strokeStyle = 'rgba(0,0,0,0.35)';
    ctx.beginPath();
    pts.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
    ctx.closePath();
    ctx.stroke();
    return c;
  }
  const g = ctx.createRadialGradient(100, 90, 20, 128, 128, 128);
  g.addColorStop(0, '#ffffff55');
  g.addColorStop(0.15, color);
  g.addColorStop(1, '#00000088');
  ctx.beginPath();
  ctx.arc(128, 128, 124, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();
  ctx.fillStyle = g;
  ctx.fill();
  ctx.lineWidth = 8;
  ctx.strokeStyle = 'rgba(255,255,255,0.55)';
  ctx.beginPath();
  ctx.arc(128, 128, 108, 0, Math.PI * 2);
  ctx.stroke();
  if (drawTimer(ctx, def.effect, color)) return c;
  // every token with a picture shows it; on the table a counter's number sits over it
  if (drawGlyph(ctx, def.effect)) return c;
  if (drawSymbol(ctx, def.id) || drawSymbol(ctx, def.effect)) return c;
  if (!def.counter) {
    // tier tokens show their numeral ("II", "III"); everything else shows its initials
    const initials =
      def.category === 'tier'
        ? (def.name.split(/\s+/).at(-1) ?? '')
        : def.name
            .split(/\s+/)
            .map((w) => w[0])
            .join('')
            .slice(0, 3)
            .toUpperCase();
    ctx.fillStyle = 'rgba(255,255,255,0.92)';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = `76px ${DISPLAY_FONT}`;
    ctx.fillText(initials, 128, 134);
  }
  return c;
}

/**
 * The turn timers are clock faces (the Count-Up Timer is teal, with an arrow pointing up at the top). On the table the counter's number covers the hands, so what tells
 * them apart there is the rim: gold with a sun at the top for "start of turn", violet with a crescent moon
 * for "end of turn". Returns false for any other token.
 */
function drawTimer(ctx: CanvasRenderingContext2D, effect: string, base: string): boolean {
  if (effect !== 'timer-start' && effect !== 'timer-end' && effect !== 'timer-up') return false;
  const start = effect === 'timer-start';
  const up = effect === 'timer-up';
  const ink = start ? '#3a2a05' : up ? '#04302b' : '#f1ecff';
  ctx.save();
  ctx.lineCap = 'round';
  // the clock's tick marks (none at 12 o'clock, where the sun or moon sits)
  ctx.strokeStyle = ink;
  for (let i = 1; i < 12; i++) {
    const a = (i / 12) * Math.PI * 2 - Math.PI / 2;
    const long = i % 3 === 0;
    ctx.lineWidth = long ? 8 : 5;
    ctx.beginPath();
    ctx.moveTo(128 + Math.cos(a) * (long ? 93 : 100), 128 + Math.sin(a) * (long ? 93 : 100));
    ctx.lineTo(128 + Math.cos(a) * 114, 128 + Math.sin(a) * 114);
    ctx.stroke();
  }
  // the hands: early morning for the start timer, evening for the end timer (seen in the Spawn menu)
  const hand = (turn: number, length: number, width: number) => {
    const a = turn * Math.PI * 2 - Math.PI / 2;
    ctx.lineWidth = width;
    ctx.beginPath();
    ctx.moveTo(128, 128);
    ctx.lineTo(128 + Math.cos(a) * length, 128 + Math.sin(a) * length);
    ctx.stroke();
  };
  hand(0, 62, 9);
  hand(start ? 0.25 : up ? 0.5 : 0.75, 40, 12);
  ctx.fillStyle = ink;
  ctx.beginPath();
  ctx.arc(128, 128, 11, 0, Math.PI * 2);
  ctx.fill();
  if (up) {
    // an arrow pointing up at the top: this one climbs
    ctx.fillStyle = '#eafff9';
    ctx.beginPath();
    ctx.moveTo(128, 6);
    ctx.lineTo(150, 30);
    ctx.lineTo(136, 30);
    ctx.lineTo(136, 44);
    ctx.lineTo(120, 44);
    ctx.lineTo(120, 30);
    ctx.lineTo(106, 30);
    ctx.closePath();
    ctx.fill();
  } else if (start) {
    // a sun at the top: a disc with short rays
    ctx.strokeStyle = '#fff6c2';
    ctx.lineWidth = 4;
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      ctx.beginPath();
      ctx.moveTo(128 + Math.cos(a) * 15, 24 + Math.sin(a) * 15);
      ctx.lineTo(128 + Math.cos(a) * 21, 24 + Math.sin(a) * 21);
      ctx.stroke();
    }
    ctx.fillStyle = '#fff6c2';
    ctx.beginPath();
    ctx.arc(128, 24, 11, 0, Math.PI * 2);
    ctx.fill();
  } else {
    // a crescent moon at the top: a pale disc with a bite taken out in the chip's own color
    ctx.fillStyle = '#f4efff';
    ctx.beginPath();
    ctx.arc(126, 25, 16, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = base;
    ctx.beginPath();
    ctx.arc(135, 20, 14, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
  return true;
}

/** A few placeholder tokens get a simple picture instead of initials. Returns false if there's none. */
function drawGlyph(ctx: CanvasRenderingContext2D, effect: string): boolean {
  ctx.save();
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  if (effect === 'locked') {
    // a medieval padlock: iron shackle, heavy body, keyhole
    ctx.strokeStyle = '#d9d2c2';
    ctx.lineWidth = 16;
    ctx.beginPath();
    ctx.moveTo(92, 124);
    ctx.lineTo(92, 96);
    ctx.arc(128, 96, 36, Math.PI, 0);
    ctx.lineTo(164, 124);
    ctx.stroke();
    ctx.fillStyle = '#3a2f1c';
    ctx.strokeStyle = '#e8d9a8';
    ctx.lineWidth = 6;
    ctx.beginPath();
    ctx.roundRect(72, 120, 112, 88, 14);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = '#e8d9a8';
    ctx.beginPath();
    ctx.arc(128, 154, 11, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillRect(123, 156, 10, 28);
    for (const [x, y] of [[84, 132], [172, 132], [84, 196], [172, 196]]) {
      ctx.beginPath();
      ctx.arc(x, y, 4, 0, Math.PI * 2);
      ctx.fill();
    }
  } else if (effect === 'entrenched') {
    // a palisade: three sharpened stakes lashed with a crossbar, dug into a mound
    ctx.fillStyle = '#3a2a14';
    ctx.strokeStyle = '#f3e3bd';
    ctx.lineWidth = 6;
    for (const [x, top] of [[86, 76], [128, 58], [170, 76]]) {
      ctx.beginPath();
      ctx.moveTo(x - 15, 188);
      ctx.lineTo(x - 15, top + 24);
      ctx.lineTo(x, top);
      ctx.lineTo(x + 15, top + 24);
      ctx.lineTo(x + 15, 188);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    }
    ctx.beginPath();
    ctx.roundRect(60, 124, 136, 20, 6);
    ctx.fill();
    ctx.stroke();
    ctx.lineWidth = 10;
    ctx.beginPath();
    ctx.moveTo(54, 198);
    ctx.quadraticCurveTo(128, 174, 202, 198);
    ctx.stroke();
  } else if (effect === 'count') {
    // a bold X: "times", for how many small units a card still has (the number replaces it on the table)
    ctx.strokeStyle = 'rgba(255,255,255,0.92)';
    ctx.lineWidth = 22;
    ctx.beginPath();
    ctx.moveTo(84, 84);
    ctx.lineTo(172, 172);
    ctx.moveTo(172, 84);
    ctx.lineTo(84, 172);
    ctx.stroke();
  } else if (effect === 'destroyed') {
    // a skull: round cranium, jaw with teeth, dark eye sockets and nose
    ctx.fillStyle = '#ece6d8';
    ctx.beginPath();
    ctx.arc(128, 112, 58, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.roundRect(94, 142, 68, 52, 12);
    ctx.fill();
    ctx.fillStyle = '#17171b';
    for (const x of [104, 152]) {
      ctx.beginPath();
      ctx.ellipse(x, 116, 15, 18, 0, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.beginPath();
    ctx.moveTo(128, 134);
    ctx.lineTo(119, 154);
    ctx.lineTo(137, 154);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = '#17171b';
    ctx.lineWidth = 5;
    for (const x of [114, 128, 142]) {
      ctx.beginPath();
      ctx.moveTo(x, 168);
      ctx.lineTo(x, 194);
      ctx.stroke();
    }
  } else if (effect === 'ground') {
    // a ball and chain
    ctx.strokeStyle = '#e6e0d2';
    ctx.lineWidth = 7;
    for (let i = 0; i < 4; i++) {
      ctx.beginPath();
      ctx.ellipse(74 + i * 20, 82 + i * 17, 13, 8, Math.PI / 4.5, 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.beginPath();
    ctx.arc(160, 158, 46, 0, Math.PI * 2);
    ctx.fillStyle = '#1b1b1e';
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = 'rgba(255,255,255,0.35)';
    ctx.beginPath();
    ctx.arc(146, 142, 12, 0, Math.PI * 2);
    ctx.fill();
  } else if (effect === 'direct' || effect === 'pierce') {
    // Direct: one bold arrow straight ahead. Pierce: the arrow runs through a bar and out the other side
    ctx.strokeStyle = 'rgba(255,255,255,0.94)';
    ctx.fillStyle = 'rgba(255,255,255,0.94)';
    ctx.lineWidth = 16;
    ctx.beginPath();
    ctx.moveTo(60, 196);
    ctx.lineTo(170, 86);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(198, 58);
    ctx.lineTo(144, 76);
    ctx.lineTo(180, 112);
    ctx.closePath();
    ctx.fill();
    if (effect === 'pierce') {
      ctx.lineWidth = 12;
      ctx.beginPath();
      ctx.moveTo(84, 92);
      ctx.lineTo(164, 172);
      ctx.stroke();
    }
  } else if (effect === 'target') {
    // a crosshair
    ctx.strokeStyle = 'rgba(255,255,255,0.94)';
    ctx.lineWidth = 11;
    for (const r of [64, 30]) {
      ctx.beginPath();
      ctx.arc(128, 128, r, 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.beginPath();
    for (const [x1, y1, x2, y2] of [[128, 46, 128, 86], [128, 170, 128, 210], [46, 128, 86, 128], [170, 128, 210, 128]]) {
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
    }
    ctx.stroke();
  } else if (effect === 'bell') {
    // a bell: flared body, a rim, the clapper below and the loop on top
    ctx.fillStyle = '#e9d9a6';
    ctx.strokeStyle = '#3a2f1c';
    ctx.lineWidth = 5;
    ctx.beginPath();
    ctx.moveTo(128, 56);
    ctx.bezierCurveTo(88, 56, 82, 104, 80, 140);
    ctx.quadraticCurveTo(78, 168, 58, 182);
    ctx.lineTo(198, 182);
    ctx.quadraticCurveTo(178, 168, 176, 140);
    ctx.bezierCurveTo(174, 104, 168, 56, 128, 56);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(128, 196, 13, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(128, 48, 11, 0, Math.PI * 2);
    ctx.stroke();
  } else if (effect === 'special-hit') {
    // a jagged burst: the card's special first hit
    const pts = 12;
    ctx.beginPath();
    for (let i = 0; i < pts * 2; i++) {
      const r = i % 2 ? 44 : 92;
      const a = (i / (pts * 2)) * Math.PI * 2 - Math.PI / 2;
      const x = 128 + Math.cos(a) * r, y = 128 + Math.sin(a) * r;
      i ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
    }
    ctx.closePath();
    ctx.fillStyle = '#ffd36b';
    ctx.strokeStyle = '#4a1a08';
    ctx.lineWidth = 6;
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = '#4a1a08';
    ctx.beginPath();
    ctx.roundRect(119, 92, 18, 46, 6);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(128, 158, 10, 0, Math.PI * 2);
    ctx.fill();
  } else if (effect.startsWith('aff-')) {
    drawAffiliation(ctx, effect.slice(4));
  } else {
    ctx.restore();
    return false;
  }
  ctx.restore();
  return true;
}

/** The symbol of an affiliation, on an affiliation token: light ink with a dark edge, inside the chip's ring. */
function drawAffiliation(ctx: CanvasRenderingContext2D, aff: string) {
  const ink = '#f4ead0';
  const edge = 'rgba(20,14,6,0.85)';
  ctx.fillStyle = ink;
  ctx.strokeStyle = edge;
  ctx.lineWidth = 6;
  const both = () => {
    ctx.fill();
    ctx.stroke();
  };
  switch (aff) {
    case 'duned': {
      // a sun over two dunes
      ctx.beginPath();
      ctx.arc(128, 98, 24, 0, Math.PI * 2);
      both();
      ctx.lineWidth = 8;
      ctx.strokeStyle = ink;
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2;
        ctx.beginPath();
        ctx.moveTo(128 + Math.cos(a) * 34, 98 + Math.sin(a) * 34);
        ctx.lineTo(128 + Math.cos(a) * 48, 98 + Math.sin(a) * 48);
        ctx.stroke();
      }
      ctx.lineWidth = 6;
      ctx.strokeStyle = edge;
      ctx.beginPath();
      ctx.moveTo(48, 186);
      ctx.quadraticCurveTo(96, 132, 150, 170);
      ctx.quadraticCurveTo(180, 150, 210, 172);
      ctx.lineTo(206, 192);
      ctx.lineTo(52, 192);
      ctx.closePath();
      both();
      break;
    }
    case 'forested': {
      // a pine tree
      for (const [top, w, base] of [[44, 38, 104], [76, 54, 146], [108, 70, 186]]) {
        ctx.beginPath();
        ctx.moveTo(128, top);
        ctx.lineTo(128 + w, base);
        ctx.lineTo(128 - w, base);
        ctx.closePath();
        both();
      }
      ctx.beginPath();
      ctx.rect(116, 186, 24, 26);
      both();
      break;
    }
    case 'frigid': {
      // a snowflake
      ctx.lineCap = 'round';
      for (const [w, c] of [[18, edge], [10, ink]] as const) {
        ctx.lineWidth = w;
        ctx.strokeStyle = c;
        for (let i = 0; i < 6; i++) {
          const a = (i / 6) * Math.PI * 2 - Math.PI / 2;
          const at = (r: number) => [128 + Math.cos(a) * r, 128 + Math.sin(a) * r];
          const [x1, y1] = at(84);
          ctx.beginPath();
          ctx.moveTo(128, 128);
          ctx.lineTo(x1, y1);
          for (const r of [40, 62]) {
            const [bx, by] = at(r);
            for (const turn of [-0.6, 0.6]) {
              ctx.moveTo(bx, by);
              ctx.lineTo(bx + Math.cos(a + turn) * 22, by + Math.sin(a + turn) * 22);
            }
          }
          ctx.stroke();
        }
      }
      break;
    }
    case 'magical': {
      // a four-pointed sparkle and a small one beside it
      const star = (cx: number, cy: number, r: number) => {
        ctx.beginPath();
        for (let i = 0; i < 8; i++) {
          const rr = i % 2 ? r * 0.28 : r;
          const a = (i / 8) * Math.PI * 2 - Math.PI / 2;
          const x = cx + Math.cos(a) * rr, y = cy + Math.sin(a) * rr;
          i ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
        }
        ctx.closePath();
        both();
      };
      star(116, 138, 82);
      star(184, 76, 30);
      break;
    }
    case 'astral': {
      // a crescent moon with a star beside it
      ctx.beginPath();
      ctx.arc(116, 130, 70, Math.PI * 0.25, Math.PI * 1.75);
      ctx.arc(150, 112, 56, Math.PI * 1.6, Math.PI * 0.4, true);
      ctx.closePath();
      both();
      ctx.beginPath();
      for (let i = 0; i < 10; i++) {
        const a = -Math.PI / 2 + (i * Math.PI) / 5;
        const r = i % 2 ? 9 : 22;
        const x = 190 + Math.cos(a) * r, y = 120 + Math.sin(a) * r;
        if (i) ctx.lineTo(x, y); else ctx.moveTo(x, y);
      }
      ctx.closePath();
      both();
      break;
    }
    case 'spectral': {
      // a ghost
      ctx.beginPath();
      ctx.moveTo(72, 200);
      ctx.lineTo(72, 112);
      ctx.arc(128, 112, 56, Math.PI, 0);
      ctx.lineTo(184, 200);
      for (let i = 0; i < 4; i++) {
        const x = 184 - (i + 1) * 28;
        ctx.quadraticCurveTo(x + 14, 178, x, 200);
      }
      ctx.closePath();
      both();
      ctx.fillStyle = edge;
      for (const x of [108, 148]) {
        ctx.beginPath();
        ctx.ellipse(x, 116, 10, 15, 0, 0, Math.PI * 2);
        ctx.fill();
      }
      break;
    }
    case 'submerged': {
      // three waves
      ctx.lineCap = 'round';
      for (const [w, c] of [[20, edge], [12, ink]] as const) {
        ctx.lineWidth = w;
        ctx.strokeStyle = c;
        for (const y of [90, 130, 170]) {
          ctx.beginPath();
          ctx.moveTo(54, y);
          for (let i = 0; i < 4; i++) ctx.quadraticCurveTo(54 + i * 37 + 18.5, y + (i % 2 ? 22 : -22), 54 + (i + 1) * 37, y);
          ctx.stroke();
        }
      }
      break;
    }
    case 'undead': {
      // a skull
      ctx.beginPath();
      ctx.arc(128, 112, 62, Math.PI * 0.85, Math.PI * 0.15);
      ctx.lineTo(166, 178);
      ctx.lineTo(166, 204);
      ctx.lineTo(90, 204);
      ctx.lineTo(90, 178);
      ctx.closePath();
      both();
      ctx.fillStyle = edge;
      for (const x of [102, 154]) {
        ctx.beginPath();
        ctx.ellipse(x, 122, 17, 20, 0, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.beginPath();
      ctx.moveTo(128, 146);
      ctx.lineTo(118, 166);
      ctx.lineTo(138, 166);
      ctx.closePath();
      ctx.fill();
      ctx.lineWidth = 4;
      for (const x of [110, 128, 146]) {
        ctx.beginPath();
        ctx.moveTo(x, 186);
        ctx.lineTo(x, 204);
        ctx.stroke();
      }
      break;
    }
    case 'volcanic': {
      // a volcano with a flame from its crater
      ctx.beginPath();
      ctx.moveTo(40, 204);
      ctx.lineTo(104, 116);
      ctx.lineTo(152, 116);
      ctx.lineTo(216, 204);
      ctx.closePath();
      both();
      ctx.fillStyle = '#ffb14a';
      ctx.beginPath();
      ctx.moveTo(108, 112);
      ctx.bezierCurveTo(96, 80, 120, 70, 116, 40);
      ctx.bezierCurveTo(140, 62, 130, 80, 140, 66);
      ctx.bezierCurveTo(160, 86, 156, 100, 148, 112);
      ctx.closePath();
      both();
      break;
    }
    default: {
      // Neutral: a balance
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.rect(122, 62, 12, 120);
      both();
      ctx.beginPath();
      ctx.rect(56, 78, 144, 12);
      both();
      ctx.beginPath();
      ctx.moveTo(96, 196);
      ctx.lineTo(160, 196);
      ctx.lineTo(148, 178);
      ctx.lineTo(108, 178);
      ctx.closePath();
      both();
      for (const x of [74, 182]) {
        ctx.beginPath();
        ctx.moveTo(x - 28, 136);
        ctx.lineTo(x + 28, 136);
        ctx.arc(x, 136, 28, 0, Math.PI);
        ctx.closePath();
        both();
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(x, 90);
        ctx.lineTo(x - 26, 136);
        ctx.moveTo(x, 90);
        ctx.lineTo(x + 26, 136);
        ctx.stroke();
        ctx.lineWidth = 6;
      }
    }
  }
}

/**
 * The placeholder mat. `px` is how many pixels wide to draw it (the game uses 2048);
 * everything is laid out on a 2048-wide grid and scaled up or down from there.
 */
export function drawMat(px = 2048): HTMLCanvasElement {
  const scale = 2048 / CONFIG.mat.width;
  const W = 2048;
  const H = Math.round(CONFIG.mat.depth * scale);
  const zoom = px / W;
  const [c, ctx] = makeCanvas(Math.round(W * zoom), Math.round(H * zoom));
  ctx.scale(zoom, zoom);
  const g = ctx.createLinearGradient(0, 0, 0, H);
  g.addColorStop(0, '#2a3336');
  g.addColorStop(0.5, '#324046');
  g.addColorStop(1, '#2a3336');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = 'rgba(214,164,67,0.55)';
  ctx.lineWidth = 6;
  ctx.strokeRect(24, 24, W - 48, H - 48);
  ctx.setLineDash([24, 18]);
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(40, H / 2);
  ctx.lineTo(W - 40, H / 2);
  ctx.stroke();
  ctx.setLineDash([]);

  const toPx = (x: number, z: number) => [(x + CONFIG.mat.width / 2) * scale, (z + CONFIG.mat.depth / 2) * scale];
  const halves: { flip: boolean; label: string }[] = [
    { flip: false, label: 'PLAYER 1 SIDE' },
    { flip: true, label: 'PLAYER 2 SIDE' },
  ];
  for (const half of halves) {
    ctx.save();
    if (half.flip) {
      ctx.translate(W, H);
      ctx.rotate(Math.PI);
    }
    const [ex, ez] = toPx(CONFIG.zones.emperor.x, CONFIG.zones.emperor.z);
    const ew = CONFIG.card.width * scale * 1.25;
    const eh = CONFIG.card.height * scale * 1.25;
    ctx.strokeStyle = 'rgba(214,164,67,0.7)';
    ctx.lineWidth = 4;
    box(ctx, ex - ew / 2, ez - eh / 2, ew, eh, 18);
    ctx.stroke();
    ctx.fillStyle = 'rgba(214,164,67,0.75)';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = `44px ${DISPLAY_FONT}`;
    ctx.fillText('EMPEROR', ex, ez - eh / 2 - 38);
    ctx.fillStyle = 'rgba(230,236,238,0.5)';
    ctx.textAlign = 'left';
    ctx.font = `56px ${DISPLAY_FONT}`;
    ctx.fillText(half.label, 72, H - 78);
    ctx.textAlign = 'right';
    ctx.font = `32px ${BODY_FONT}`;
    ctx.fillText('Placeholder mat — replace public/mat/mat.png', W - 96, H - 78, W / 2 - 140);
    ctx.restore();
  }
  return c;
}

function drawBar(bar: 'gerzite' | 'dark'): HTMLCanvasElement {
  const b = bar === 'gerzite' ? CONFIG.zones.gerziteBar : CONFIG.zones.darkGerziteBar;
  const s = 24;
  const [c, ctx] = makeCanvas(Math.round(b.width * s), Math.round(b.depth * s));
  const color = hexToRgb(bar === 'gerzite' ? CONFIG.colors.gerziteBar : CONFIG.colors.darkGerziteBar);
  ctx.fillStyle = `rgba(${color},0.12)`;
  box(ctx, 3, 3, c.width - 6, c.height - 6, 14);
  ctx.fill();
  ctx.strokeStyle = `rgba(${color},0.9)`;
  ctx.lineWidth = 4;
  ctx.stroke();
  const rows = Math.ceil(b.slots / b.columns);
  const sx = c.width / b.columns;
  const sz = c.height / rows;
  ctx.strokeStyle = `rgba(${color},0.4)`;
  ctx.lineWidth = 2;
  for (let i = 0; i < b.slots; i++) {
    const col = i % b.columns;
    const row = Math.floor(i / b.columns);
    ctx.beginPath();
    ctx.arc(sx * (col + 0.5), sz * (row + 0.5), Math.min(sx, sz) * 0.38, 0, Math.PI * 2);
    ctx.stroke();
  }
  return c;
}

// ---------------- finding images ----------------

const cache = new Map<string, Promise<string>>();

/** "gerzite" works the same as "gerzite.png": a name with no ending is treated as a PNG. */
function withExt(name: string) {
  const file = name.trim();
  return /\.[a-z0-9]{2,5}$/i.test(file) ? file : `${file}.png`;
}

/** Only used by the single-file preview, which carries its images inside the page. */
function inlineAsset(file: string): string | undefined {
  return (globalThis as { __INLINE_ASSETS__?: Record<string, string> }).__INLINE_ASSETS__?.[file];
}

function resolve(key: string, file: string, placeholder: () => HTMLCanvasElement): Promise<string> {
  let p = cache.get(key);
  if (p) return p;
  p = new Promise<string>((done) => {
    const inline = file ? inlineAsset(file) : undefined;
    if (inline) {
      done(inline);
      return;
    }
    if (!__ASSETS__ || !file) {
      toBlobUrl(placeholder()).then(done);
      return;
    }
    // the online build keeps lighter copies of the big pictures; use one if there is one, then the picture
    // itself under each name it might have ("chicken.PNG" for "chicken.png")
    const tries = [lighterOf(file), ...pictureNames(file)].filter((f): f is string => !!f);
    const img = new Image();
    let k = 0;
    img.onload = () => done(tries[k]);
    img.onerror = () => {
      if (++k < tries.length) {
        img.src = tries[k];
        return;
      }
      console.warn(`Image not found: public/${file} — using a placeholder.`);
      toBlobUrl(placeholder()).then(done);
    };
    img.src = tries[0];
  });
  cache.set(key, p);
  return p;
}

/** A card's face. `dark`: it was summoned with Dark Gerzite, so its cost gem glows dark red. */
export function cardFrontSrc(num: number, dark = false, form = 0): Promise<string> {
  const card = CARD_DB.byNumber.get(num);
  if (!card) return cardBackSrc();
  if (!card.formImages?.[form]) form = 0;
  // drawn from the card templates (cardFace.ts); a card kind with no template yet keeps the older look
  if (hasFace(card)) {
    const key = `face:${num}${dark ? ':dark' : ''}${form ? `:f${form}` : ''}`;
    let p = cache.get(key);
    if (!p) {
      p = cardFaceSrc(card, dark, form).then((url) => url ?? resolve(`card:${num}`, '', () => drawCard(card)));
      cache.set(key, p);
    }
    return p;
  }
  return resolve(`card:${num}`, card.image ? CONFIG.images.cardFolder + withExt(card.image) : '', () => drawCard(card));
}

export function cardBackSrc(): Promise<string> {
  return resolve('back', CONFIG.images.cardBack, drawCardBack);
}

export function tokenSrc(tokenId: string): Promise<string> {
  const def = TOKEN_DB.byId.get(tokenId);
  if (!def) return resolve('token:?', '', () => makeCanvas(4, 4)[0]);
  return resolve(`token:${tokenId}`, def.image ? CONFIG.images.tokenFolder + withExt(def.image) : '', () => drawToken(def));
}

let menuArt: Promise<string | null> | null = null;
/**
 * The picture behind the main menu (images.menu in the settings), or null if there isn't one: then the
 * menu keeps its plain look with the fan of card backs.
 */
export function menuArtSrc(): Promise<string | null> {
  if (menuArt) return menuArt;
  const file = CONFIG.images.menu;
  menuArt = new Promise<string | null>((done) => {
    const inline = file ? inlineAsset(file) : undefined;
    if (inline) return done(inline);
    if (!__ASSETS__ || !file) return done(null);
    // the online build keeps a lighter copy of it; use that if there is one
    const lighter = lighterOf(file);
    const img = new Image();
    let triedOriginal = !lighter;
    img.onload = () => done(triedOriginal ? file : lighter!);
    img.onerror = () => {
      if (!triedOriginal) {
        triedOriginal = true;
        img.src = file;
        return;
      }
      done(null);
    };
    img.src = lighter ?? file;
  });
  return menuArt;
}

export function matSrc(): Promise<string> {
  // (kept per picture: the 2v2 table has a mat of its own, so a 1v1 game earlier in the visit mustn't leave its mat behind)
  return resolve(`mat:${CONFIG.images.mat}:${CONFIG.mat.width}x${CONFIG.mat.depth}`, CONFIG.images.mat, drawMat);
}

export function barSrc(bar: 'gerzite' | 'dark'): Promise<string> {
  const file = bar === 'gerzite' ? CONFIG.images.gerziteBar : CONFIG.images.darkGerziteBar;
  return resolve(`bar:${bar}`, file, () => drawBar(bar));
}

/** Points an <img> at a picture once it's ready. */
export function setImg(img: HTMLImageElement, src: Promise<string>) {
  src.then((s) => {
    img.src = s;
  });
  return img;
}

let lazyWatch: IntersectionObserver | null = null;
const lazyLoaders = new WeakMap<Element, () => Promise<string>>();
/**
 * Like setImg, but the picture is only made or fetched once the <img> is on (or near) the screen.
 * With hundreds of cards in a list, this keeps the list quick to open.
 */
export function setImgLazy(img: HTMLImageElement, src: () => Promise<string>) {
  if (typeof IntersectionObserver === 'undefined') return setImg(img, src());
  lazyWatch ??= new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (!e.isIntersecting) continue;
        const load = lazyLoaders.get(e.target);
        lazyWatch!.unobserve(e.target);
        lazyLoaders.delete(e.target);
        if (load) setImg(e.target as HTMLImageElement, load());
      }
    },
    { rootMargin: '600px' },
  );
  lazyLoaders.set(img, src);
  lazyWatch.observe(img);
  return img;
}

/** Loads a picture as an <img> element (for drawing onto canvases). */
export function loadImageEl(src: string): Promise<HTMLImageElement> {
  return new Promise((done, fail) => {
    const img = new Image();
    img.onload = () => done(img);
    img.onerror = fail;
    img.src = src;
  });
}

/** Wait for the game's fonts so placeholders are drawn with them. */
export async function fontsReady() {
  try {
    await Promise.race([
      Promise.all([
        document.fonts.load(`34px ${DISPLAY_FONT}`),
        document.fonts.load(`600 26px ${BODY_FONT}`),
        document.fonts.load(`700 26px ${BODY_FONT}`),
      ]),
      new Promise((r) => setTimeout(r, 2500)),
    ]);
  } catch {
    /* fall back to system fonts */
  }
}
